import type {
  TAuthPresetSpec,
  TFailedStep,
  TSender,
  TSignatureParse,
  TTimestampLocation,
  TVerifyResult,
} from './api';

export const readsSummary = (spec: TAuthPresetSpec): string => {
  const { header, parse } = spec.signature.location;
  return `${header} · ${parseSummary(parse)}`;
};

export const parseSummary = (parse: TSignatureParse): string => {
  if (parse.kind === 'raw') return 'raw';
  if (parse.kind === 'prefixed') return parse.prefix;
  return `kv ${parse.signatureKey}`;
};

export interface ITimeSummary {
  text: string;
  replayable: boolean;
}

export const timeSummary = (spec: TAuthPresetSpec): ITimeSummary => {
  const location = spec.timestamp.location;
  if (location.kind === 'none') return { text: 'not signed — replayable', replayable: true };

  const tolerance = spec.timestamp.defaultToleranceSec;
  if (location.kind === 'header') return { text: 'signed, separate header', replayable: false };
  return {
    text: tolerance ? `signed, ${tolerance} s window` : 'signed, no window set',
    replayable: false,
  };
};

export const isReplayable = (spec: TAuthPresetSpec): boolean =>
  spec.timestamp.location.kind === 'none';

export const secretPrefixOf = (spec: TAuthPresetSpec): string | null =>
  spec.signature.secretPrefix ?? null;

export const timestampSummary = (location: TTimestampLocation): string => {
  if (location.kind === 'none') return 'nowhere';
  if (location.kind === 'header') return `the header ${location.header}`;
  return `the same header, key ${location.key}`;
};

export const senderMonogram = (name: string): string => {
  const letters = name.replace(/[^A-Za-z0-9]/g, '').slice(0, 2);
  return letters.charAt(0).toUpperCase() + letters.slice(1).toLowerCase();
};

export const senderLine = (sender: TSender, withShape: boolean): string =>
  withShape ? `${sender.signature.header} · ${sender.signature.shape}` : sender.signature.header;

export const senderReplayable = (sender: TSender): boolean => !sender.signsTimestamp;

export const sourcesUsing = (senders: readonly TSender[]): number =>
  senders.reduce((total, sender) => total + sender.usedByActiveSourceCount, 0);

export type TTemplatePart =
  { kind: 'placeholder'; token: string } | { kind: 'literal'; text: string };

export const PLACEHOLDER_LABELS: Record<string, string> = {
  '{rawBody}': 'the body',
  '{timestamp}': 'the time',
  '{method}': 'the method',
  '{path}': 'the path',
  '{url}': 'the url',
  '{query}': 'the query string',
  '{sortedFormParams}': 'the form fields, sorted',
};

export const placeholderLabel = (token: string): string => {
  const header = /^\{header:(.+)\}$/.exec(token);
  if (header) return `the header ${header[1]}`;
  return PLACEHOLDER_LABELS[token] ?? token;
};

// `{{` is an escaped brace: literal text, not the start of a placeholder.
export const parseTemplate = (template: string): TTemplatePart[] => {
  const parts: TTemplatePart[] = [];
  let literal = '';
  let index = 0;

  while (index < template.length) {
    if (template.startsWith('{{', index)) {
      literal += '{';
      index += 2;
      continue;
    }
    if (template[index] === '{') {
      const close = template.indexOf('}', index);
      if (close > index) {
        if (literal) parts.push({ kind: 'literal', text: literal });
        literal = '';
        parts.push({ kind: 'placeholder', token: template.slice(index, close + 1) });
        index = close + 1;
        continue;
      }
    }
    literal += template[index];
    index += 1;
  }
  if (literal) parts.push({ kind: 'literal', text: literal });
  return parts;
};

export const buildTemplate = (parts: readonly TTemplatePart[]): string =>
  parts.map((part) => (part.kind === 'placeholder' ? part.token : part.text)).join('');

export const ADDABLE_PLACEHOLDERS = [
  '{rawBody}',
  '{timestamp}',
  '{method}',
  '{path}',
  '{url}',
  '{query}',
  '{sortedFormParams}',
] as const;

export type TStepState = 'ok' | 'failed' | 'unknown' | 'skipped';

export interface IVerifyStep {
  key: TFailedStep;
  title: string;
  fact: string;
  state: TStepState;
  verbatim?: boolean;
}

const STEP_ORDER: TFailedStep[] = ['header', 'parse', 'string', 'timestamp', 'compute', 'compare'];

const parseTitle = (kind: TVerifyResult['parseKind']): string =>
  kind === 'kv' ? 'Pairs read' : kind === 'prefixed' ? 'Prefix stripped' : 'Value taken';

const parseFact = (result: TVerifyResult): string => {
  if (result.parseKind === 'kv') {
    if (result.headerFields.length === 0) return 'no pairs came out of the header';
    return result.headerFields
      .map((field) => `${field.key} = ${field.value}${field.truncated ? '…' : ''}`)
      .join(' · ');
  }
  if (result.signatureCandidates.length === 0) return 'nothing was taken from the header';
  return result.signatureCandidates.map((candidate) => `${candidate}…`).join(' · ');
};

const timeFact = (result: TVerifyResult): string => {
  if (result.toleranceSec === null || result.toleranceSec === undefined) {
    return 'this scheme signs no time, so a captured request stays valid forever';
  }
  if (result.timestampText === null || result.timestampText === undefined) {
    return `no time found, and the scheme expects one inside ${result.toleranceSec} s`;
  }
  const skew = result.timestampSkewMs ?? null;
  const age = skew === null ? 'age unknown' : `${Math.round(Math.abs(skew) / 1000)} s old`;
  const verdict = result.timestampWithinTolerance === false ? 'outside' : 'inside';
  return `${age}, ${verdict} ${result.toleranceSec} s`;
};

const computeFact = (result: TVerifyResult, spec: TAuthPresetSpec | null): string => {
  if (!result.secretChecked) return 'nothing computed — no secret entered yet';
  const how = spec ? `hmac-${spec.signature.algorithm} · ${spec.signature.encoding} → ` : '';
  const length = result.computedSignatureLength;
  const tail = length ? ` (${length} characters)` : '';
  return `${how}${result.computedSignature ?? '—'}…${tail}`;
};

const compareFact = (result: TVerifyResult): string => {
  if (result.signatureMatched === null || result.signatureMatched === undefined) {
    return 'not compared: the secret has not been entered, so nothing was computed';
  }
  if (result.signatureMatched) return 'same as the header';
  const candidate = result.signatureCandidates[0] ?? '—';
  return `${result.computedSignature ?? '—'}… against ${candidate}… — different`;
};

const stateFor = (step: TFailedStep, result: TVerifyResult): TStepState => {
  const failed = result.failedStep ?? null;
  if (failed) {
    const at = STEP_ORDER.indexOf(failed);
    const here = STEP_ORDER.indexOf(step);
    if (here === at) return 'failed';
    return here < at ? 'ok' : 'skipped';
  }
  if (!result.secretChecked && (step === 'compute' || step === 'compare')) return 'unknown';
  return 'ok';
};

export const verifySteps = (result: TVerifyResult, spec: TAuthPresetSpec | null): IVerifyStep[] => [
  {
    key: 'header',
    title: 'Header found',
    fact: result.headerFound ? result.headerName : `${result.headerName} — not in this request`,
    state: stateFor('header', result),
  },
  {
    key: 'parse',
    title: parseTitle(result.parseKind),
    fact: parseFact(result),
    state: stateFor('parse', result),
  },
  {
    key: 'string',
    title: 'String built',
    fact: result.signedString ?? 'nothing was built',
    state: stateFor('string', result),
    verbatim: true,
  },
  {
    key: 'timestamp',
    title: 'Time checked',
    fact: timeFact(result),
    state: stateFor('timestamp', result),
  },
  {
    key: 'compute',
    title: 'Computed',
    fact: computeFact(result, spec),
    state: stateFor('compute', result),
  },
  {
    key: 'compare',
    title: 'Compared',
    fact: compareFact(result),
    state: stateFor('compare', result),
  },
];

export type TVerdict = 'matches' | 'does not match' | 'reading only';

export const verdictOf = (result: TVerifyResult): TVerdict => {
  if (!result.secretChecked) return 'reading only';
  return result.signatureMatched ? 'matches' : 'does not match';
};

export const slugKey = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

export const blankSpec = (): TAuthPresetSpec => ({
  schemaVersion: 1,
  key: '',
  name: '',
  signature: {
    algorithm: 'sha256',
    encoding: 'hex',
    secretEncoding: 'utf8',
    template: '{rawBody}',
    location: { header: 'X-Signature', parse: { kind: 'raw' } },
  },
  timestamp: { location: { kind: 'none' } },
});

export const specText = (spec: TAuthPresetSpec): string => JSON.stringify(spec, null, 2);

export const parseHeaderLines = (text: string): Record<string, string> => {
  const headers: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const at = line.indexOf(':');
    if (at <= 0) continue;
    const name = line.slice(0, at).trim();
    if (name) headers[name] = line.slice(at + 1).trim();
  }
  return headers;
};

export const headerLines = (headers: Record<string, string>): string =>
  Object.entries(headers)
    .map(([name, value]) => `${name}: ${value}`)
    .join('\n');
